import { CarouselInfoCardData, CarouselInfoCardEvents, CarouselInfoCardPointerPosition } from '../types';
import { GzCarouselInfoCardWebComponent } from './GzCarouselInfoCardWebComponent';
import { getCarouselInfoCardDefaultStrings } from '../types/strings';

export class GzCarouselInfoOverlay {
  static show(
    section: string,
    cardsData: CarouselInfoCardData[],
    targetElements: HTMLElement | HTMLElement[],
    closeBtnTooltipText: string,
    currentCardIndex = 0
  ): GzCarouselInfoCardWebComponent | undefined {
    // targetElements should be a single HTMLElement or an array of HTMLElements with the same length as cardsData
    if (Array.isArray(targetElements)) {
      if ((targetElements as any).length !== cardsData.length) {
        console.error(
          'targetElements should be a single HTMLElement or an array of HTMLElements with the same length as cardsData'
        );
        return;
      }
      if (cardsData.length === 0) {
        console.error('targetElements array should not be empty');
        return;
      }
    }

    const carouselComponent = new GzCarouselInfoCardWebComponent();

    carouselComponent.currentCardIndex = currentCardIndex;
    carouselComponent.cardsData = cardsData;
    carouselComponent.strings = getCarouselInfoCardDefaultStrings();
    carouselComponent.id = `carousel-component-${section}`;
    carouselComponent.closeBtnTooltipText = closeBtnTooltipText;

    document.body.appendChild(carouselComponent);

    const initialElement = Array.isArray(targetElements) ? targetElements[currentCardIndex] : targetElements;
    carouselComponent.updateComplete.then(() => {
      GzCarouselInfoOverlay.updatePosition(carouselComponent, initialElement);
    });

    carouselComponent.addEventListener(CarouselInfoCardEvents.CLOSE, () => {
      carouselComponent.remove();
    });
    if (Array.isArray(targetElements)) {
      carouselComponent.addEventListener(CarouselInfoCardEvents.PREVIOUS, () => {
        const targetElement = targetElements[carouselComponent.currentCardIndex];
        GzCarouselInfoOverlay.updatePosition(carouselComponent, targetElement);
      });
      carouselComponent.addEventListener(CarouselInfoCardEvents.NEXT, () => {
        const targetElement = targetElements[carouselComponent.currentCardIndex];
        GzCarouselInfoOverlay.updatePosition(carouselComponent, targetElement);
      });
    }

    return carouselComponent;
  }

  private static updatePosition(carousel: GzCarouselInfoCardWebComponent, targetElement: HTMLElement): void {
    if (!carousel?.currentCardData || !targetElement) {
      return;
    }
    const targetRect = targetElement.getBoundingClientRect();
    const pointerHeight = 12; // the carousel pointer triangle height (in px)
    const pointerWidth = 18; // the carousel pointer triangle width (in px)
    const cornerOffset = 16; // the offset of the pointer triangle from the carousel corner (in px)
    const space = 4; // the space between the pointer and the target element (in px)

    const pointerDistance = pointerHeight + space;
    const pointerOffset = cornerOffset + pointerWidth / 2;
    const targetXCenter = targetRect.left + targetRect.width / 2;
    const targetYCenter = targetRect.top + targetRect.height / 2;
    const carouselWidth = carousel.getBoundingClientRect().width;

    switch (carousel.currentCardData.pointerPosition) {
      case CarouselInfoCardPointerPosition.TOP_BORDER_LEFT_SIDE:
        carousel.style.top = `${targetRect.bottom + pointerDistance}px`;
        carousel.style.bottom = 'unset';
        carousel.style.left = `${targetXCenter - pointerOffset}px`;
        break;
      case CarouselInfoCardPointerPosition.TOP_BORDER_RIGHT_SIDE:
        carousel.style.top = `${targetRect.bottom + pointerDistance}px`;
        carousel.style.bottom = 'unset';
        carousel.style.left = `${targetXCenter + pointerOffset - carouselWidth}px`;
        break;
      case CarouselInfoCardPointerPosition.BOTTOM_BORDER_LEFT_SIDE:
        carousel.style.top = 'unset';
        carousel.style.bottom = `${window.innerHeight - targetRect.top + pointerDistance}px`;
        carousel.style.left = `${targetXCenter - pointerOffset}px`;
        break;
      case CarouselInfoCardPointerPosition.BOTTOM_BORDER_RIGHT_SIDE:
        carousel.style.top = 'unset';
        carousel.style.bottom = `${window.innerHeight - targetRect.top + pointerDistance}px`;
        carousel.style.left = `${targetXCenter + pointerOffset - carouselWidth}px`;
        break;
      case CarouselInfoCardPointerPosition.LEFT_BORDER_UPPER_SIDE:
        carousel.style.top = `${targetYCenter - pointerOffset}px`;
        carousel.style.bottom = 'unset';
        carousel.style.left = `${targetRect.right + pointerDistance}px`;
        break;
      case CarouselInfoCardPointerPosition.LEFT_BORDER_LOWER_SIDE:
        carousel.style.top = 'unset';
        carousel.style.bottom = `${window.innerHeight - targetYCenter - pointerOffset}px`;
        carousel.style.left = `${targetRect.right + pointerDistance}px`;
        break;
      case CarouselInfoCardPointerPosition.RIGHT_BORDER_UPPER_SIDE:
        carousel.style.top = `${targetYCenter - pointerOffset}px`;
        carousel.style.bottom = 'unset';
        carousel.style.left = `${targetRect.left - pointerDistance - carouselWidth}px`;
        break;
      case CarouselInfoCardPointerPosition.RIGHT_BORDER_LOWER_SIDE:
        carousel.style.top = 'unset';
        carousel.style.bottom = `${window.innerHeight - targetYCenter - pointerOffset}px`;
        carousel.style.left = `${targetRect.left - pointerDistance - carouselWidth}px`;
        break;
    }
  }
}
